export const LANDING_PAGE_ROUTE = '/login';

export const SPARKRX_PDT_UID = '8dpNPSmSnyN3vxeBi4RtM9';
export const SLEEPIO_PDT_UID = 'BxgXBrgwswcX58wFNwhVfv';
export const DAYLIGHT_PDT_UID = 'DhZzR9fNJyNGMETp6Qgr6K';
export const DAYLIGHT_PLATGEN_PDT_UID = 'WcLGEey8ZMtnpoKBDrWH26';

// https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s01.html
// eslint-disable-next-line
export const EMAIL_VALIDATION_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

// RTX's internal authentication alias domain — can't receive mail.
// Only supports one domain today; can be extended to a list later if needed.
export const BLOCKED_SIGNUP_EMAIL_DOMAIN = 'adxuser.com';

// https://ihateregex.io/expr/phone/
export const PHONE_VALIDATION_REGEX = /^[+]?\(?\d{3}\)?[-\s.]?\d{3}[-\s.]?\d{4,6}$/i;

// Validates this format: MM/DD/YYYY
export const DOB_VALIDATION_REGEX = /(?:(09|04|06|11)(\/)(0[1-9]|[12]\d|30)(\/)((?:19|20)\d\d))|(?:(01|03|05|07|08|10|12)(\/)(0[1-9]|[12]\d|3[01])(\/)((?:19|20)\d\d))|(?:02(\/)(?:(?:(0[1-9]|1\d|2[0-8])(\/)((?:19|20)\d\d))|(?:(29)(\/)((?:(?:19|20)(?:04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96))|2000))))/; // eslint-disable-line

// Validates zipcodes in these formats: #####
export const ZIPCODE_VALIDATION_REGEX = /^(\d{5})$/;

/* Validates password with these rules:
 - 8-24 characters
 - Include letters, numbers, and special characters
 */
export const PASSWORD_VALIDATION_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,24}$/;

export const EN = 'EN';
export const ES = 'ES';

export const LANGUAGE_LIST = [
  { value: EN, label: 'English' },
  { value: ES, label: 'Español' },
];

export const MINIMUM_BIRTH_YEAR = 1900;

export const SINGULAR_SDK_KEY = 'bighealth_8b9fd55c';
export const SINGULAR_SDK_SECRET = 'c65265155b6d1a8de24c00a5570aad38';

export const EMPLOYER_ORG_SLUG_KEY = 'employer_org_slug';
export const REDIRECT_TO_SESSION_KEY = 'cap_redirect_to';

/**
 * Every address CAP will hand a finished Sleepio session to, in full.
 *
 * Mirrors `VALID_REDIRECT_TARGETS` in vercel-auth-functions `lib/redirectAllowlist.ts` — one
 * entry per scheme the Sleepio app registers. There is no package shared between the two
 * repos, so this is the second of two statements of the same set; change one and the other
 * has to move with it. Both repos' contract tests write the strings out in full so a
 * disagreement shows up as a diff rather than as a failed sign-in.
 *
 * **Exact match, not "starts with an allowed scheme."** A scheme check admits
 * `sleepio://anything?attacker=controlled`, and this is the value the one-time exchange token
 * gets attached to. The auth service checks its own copy, but it only sees the address that
 * goes into the email — on the phone-OTP path it is never asked at all, so for that path this
 * list is the only thing deciding where a member's session is delivered.
 */
/**
 * The one query parameter a handoff address may carry: the app's per-flow nonce.
 *
 * The app mints an unguessable value when it starts a sign-in, appends it to the address it asks
 * us to send it back to, and refuses any handoff that does not carry it back. That is what binds
 * a returning session to the flow that asked for it. CAP is a pure carrier — it never mints,
 * reads, or checks the value, only permits it to travel.
 *
 * Mirrors `HANDOFF_NONCE_PARAM` in vercel-auth-functions `lib/redirectAllowlist.ts`; both repos
 * write the name out so a disagreement shows up as a diff. **Optional by design** — an app build
 * that predates the nonce sends no query and stays legal, which it must, because an installed app
 * is never fully replaced.
 */
export const HANDOFF_NONCE_PARAM = 'hn';

export const CANOPY_POST_AUTH_CALLBACK_TARGETS = [
  'sleep-companion://exchange-callback',
  'sleepio://exchange-callback',
] as const;

/**
 * Where the app should be re-opened once authentication finishes — the default used when the
 * app supplied no address of its own. Named separately from the list above because a default
 * is a different thing from a permitted value.
 *
 * There is one address, not two. By the time CAP hands back to the app it is always
 * holding a short-lived `exchange_token`, whichever journey the member took, and
 * `exchange-callback` is the screen that redeems one.
 *
 * The app's other entry point, `authCallback`, is a different stage of the flow: it
 * receives the emailed link itself, carrying a Supabase verify URL in `_p`. Sent an
 * `exchange_token` instead, it has no `_p` to work with and bounces the member back to
 * the activation screen — so a returning member who did everything right lands on
 * sign-up. Choosing between the two addresses by guessing the member's intent is what
 * produced that (PAT-1286); the two constants this replaces encoded the guess.
 *
 * This value is a default, never a decision. It is used only when the app supplied no
 * address of its own, because the app is the only author of its own return address.
 *
 * **This constant is meant to be deleted.** It is a bridge, kept only because links
 * already sitting in members' inboxes were sent before the auth service learned to carry
 * the app's address, and those links stay valid for as long as their token lives. Three
 * things have to be true before it can go: the auth-service change is deployed, one token
 * lifetime has passed since then, and there is a real screen for the no-address case
 * (DAYL-1136 — today that path sets no handoff URL, so the "Open Sleepio" button has
 * nothing to open). Until DAYL-1136 exists, deleting this strands anyone who opened the
 * link on a desktop.
 */
export const CANOPY_POST_AUTH_CALLBACK = 'sleep-companion://exchange-callback';

/**
 * Stand-in for the `:org_slug` segment on the Sleepio login route.
 *
 * A login magic link carries no organisation — vercel-auth-functions builds that URL and
 * has none to put in it — but the route pattern requires the segment, and an empty one
 * leaves a `//` that matches nothing. The login path never reads the value, so any
 * non-empty string works; this is named so it can be grepped and so a Cortex-facing call
 * can assert it never receives it.
 */
export const LOGIN_ORG_SLUG_PLACEHOLDER = 'unknown';
export const ACCESS_CODE_SESSION_KEY = 'cap_access_code';
export const DEPENDENT_ACTIVATION_DATA_KEY = 'dependent_activation_data';

export type LineOfBusiness = 'wellness' | 'rx';

/**
 * Sleepio Activation Flow A/B Experiment
 *
 * - VARIANT_A: LineStep - horizontal progress bar with 3 lines
 * - VARIANT_B: ListStep - numbered list showing steps with durations
 */
export const VARIANT_A = 'variant-a';
export const VARIANT_B = 'variant-b';

export const ONBOARDING_STEPS = [
  { titleKey: 'onboarding.steps.verify_email', durationKey: 'onboarding.steps.duration_30_seconds' },
  { titleKey: 'onboarding.steps.confirm_coverage', durationKey: 'onboarding.steps.duration_1_minute' },
  { titleKey: 'onboarding.steps.download_sleepio', durationKey: 'onboarding.steps.duration_1_minute' },
];
